// Клиент HTTP API сервера (web/server/stoweb/routes). CSRF-токен сессии - из <meta name="sto-token"> (сервер
// подставляет его вошедшему) или из ответа входа (setApiToken). Потеря входа посреди работы (401 auth_required)
// и устаревший токен (403 csrf) обрабатывает функция auth через authHooks: запрос ждёт повторного входа и
// повторяется - несохранённые правки не теряются.
import type { AssetInfo, BuildResult, JobState, ProjectInfo, ReportFile } from './types';

let token = (document.querySelector('meta[name="sto-token"]') as HTMLMetaElement | null)?.content || '';
if (token.startsWith('%%')) token = '';            // index.html без подстановки сервера

/** Новый токен сессии (после входа, смены пароля); обновляет и <meta name="sto-token">. */
export function setApiToken(t: string) {
  token = t || '';
  const m = document.querySelector('meta[name="sto-token"]');
  if (m) m.setAttribute('content', token);
}
export function getApiToken() { return token; }

export class ApiError extends Error {
  /** data - весь ответ сервера с ошибкой (кроме detail/code - доп. поля: savedBy при конфликте, reason при 401…) */
  constructor(message: string, public status: number, public code?: string, public data?: any) { super(message); }
}

/** Обработчики состояния входа (ставит функция auth). */
export const authHooks: {
  /** 401 auth_required: показать вход и дождаться его; true - повторить запрос */
  relogin?: (e: ApiError) => Promise<boolean>;
  /** 403 csrf: взять свежий токен (GET /api/auth/me); true - повторить запрос */
  refresh?: () => Promise<boolean>;
  /** 403 password_change_required: показать смену временного пароля */
  passwordChange?: () => void;
  /** 402 subscription_required: подписка не активна - только просмотр */
  subscription?: () => void;
} = {};

async function request<T>(method: string, url: string, body?: unknown, raw?: BodyInit, headers?: Record<string, string>, attempt = 0): Promise<T> {
  const h: Record<string, string> = { 'X-STO-Token': token, ...(headers || {}) };
  let payload: BodyInit | undefined = raw;
  if (body !== undefined) {
    h['Content-Type'] = 'application/json';
    payload = JSON.stringify(body);
  }
  let res: Response;
  try {
    res = await fetch(url, { method, headers: h, body: payload });
  } catch (e) {
    throw new ApiError('Нет связи с сервером редактора. Запущен ли «Редактор СТО.cmd»?', 0);
  }
  if (!res.ok) {
    let msg = res.statusText;
    let code: string | undefined;
    let data: any;
    try { const j = await res.json(); data = j; msg = j.detail || msg; code = j.code; } catch { /* текст ошибки не JSON */ }
    const err = new ApiError(typeof msg === 'string' ? msg : JSON.stringify(msg), res.status, code, data);
    if (attempt < 2) {
      // 401/403 от защиты сервера: запрос не выполнялся - после входа или обновления токена его можно повторить
      if (res.status === 401 && code === 'auth_required' && authHooks.relogin && await authHooks.relogin(err)) {
        return request<T>(method, url, body, raw, headers, attempt + 1);
      }
      if (res.status === 403 && code === 'csrf' && authHooks.refresh && await authHooks.refresh()) {
        return request<T>(method, url, body, raw, headers, attempt + 1);
      }
    }
    if (res.status === 403 && code === 'password_change_required') authHooks.passwordChange?.();
    if (res.status === 402 && code === 'subscription_required') authHooks.subscription?.();
    throw err;
  }
  const ct = res.headers.get('content-type') || '';
  return (ct.includes('application/json') ? res.json() : res.text()) as Promise<T>;
}

const enc = encodeURIComponent;
const P = (pid: string) => `/api/projects/${enc(pid)}`;

export const api = {
  get token() { return token; },
  get: <T>(url: string) => request<T>('GET', url),
  post: <T>(url: string, body?: unknown) => request<T>('POST', url, body ?? {}),
  put: <T>(url: string, body?: unknown) => request<T>('PUT', url, body ?? {}),
  patch: <T>(url: string, body?: unknown) => request<T>('PATCH', url, body ?? {}),
  del: <T>(url: string, body?: unknown) => request<T>('DELETE', url, body),

  info: () => request<{ version: string; dataDir: string; word: boolean; officeXsl: boolean }>('GET', '/api/info'),
  defaults: () => request<any>('GET', '/api/defaults'),

  // --- проекты
  listProjects: () => request<{ projects: ProjectInfo[]; dataDir: string }>('GET', '/api/projects'),
  createProject: (p: { name: string; type: string; template?: string; meta?: Record<string, unknown> }) =>
    request<{ id: string; report: ReportFile }>('POST', '/api/projects', p),
  loadProject: (pid: string) => request<ReportFile>('GET', P(pid)),
  saveProject: (pid: string, data: Partial<ReportFile> & { baseRevision?: number; force?: boolean }) =>
    request<{ revision: number; modified: string }>('PUT', P(pid), data),
  renameProject: (pid: string, name: string) => request<{ id: string }>('POST', `${P(pid)}/rename`, { name }),
  duplicateProject: (pid: string, name?: string) => request<{ id: string }>('POST', `${P(pid)}/duplicate`, { name }),
  trashProject: (pid: string) => request<{ trashed: string }>('DELETE', P(pid)),
  importProject: (file: File, name?: string) =>
    request<{ id: string }>('POST', `/api/projects-import?filename=${enc(file.name)}&name=${enc(name || '')}`, undefined, file),

  // --- версии
  versions: (pid: string) => request<{ versions: { id: string; kind: string; label: string; time: string }[] }>('GET', `${P(pid)}/versions`),
  snapshot: (pid: string, label = '') => request<{ id: string }>('POST', `${P(pid)}/versions`, { label }),
  loadVersion: (pid: string, vid: string) => request<ReportFile>('GET', `${P(pid)}/versions/${enc(vid)}`),
  restoreVersion: (pid: string, vid: string) => request<{ revision: number }>('POST', `${P(pid)}/versions/${enc(vid)}/restore`),

  // --- изображения
  uploadAsset: (pid: string, blob: Blob, filename = '') =>
    request<AssetInfo>('POST', `${P(pid)}/assets?filename=${enc(filename)}`, undefined, blob,
      { 'Content-Type': blob.type || 'application/octet-stream' }),
  assetUrl: (pid: string, name: string, thumb = 0) => `${P(pid)}/assets/${enc(name)}${thumb ? `?thumb=${thumb}` : ''}`,
  listAssets: (pid: string) => request<{ assets: AssetInfo[] }>('GET', `${P(pid)}/assets`),
  pasteLocalImage: (pid: string, url: string) => request<AssetInfo>('POST', `${P(pid)}/paste/local-image`, { url }),

  // --- сборка, экспорт, проверка
  build: (pid: string, opts: { pdf?: boolean; check?: boolean; word?: boolean }) =>
    request<{ job: string }>('POST', `${P(pid)}/build`, opts),
  exportFile: (pid: string, format: 'docx' | 'pdf' | 'md' | 'zip') =>
    request<{ job?: string; file?: string }>('POST', `${P(pid)}/export`, { format }),
  job: <T = BuildResult>(jid: string) => request<JobState<T>>('GET', `/api/jobs/${enc(jid)}`),
  fileUrl: (pid: string, name: string, inline = false) => `${P(pid)}/files/${name.split('/').map(enc).join('/')}${inline ? '?inline=1' : ''}`,
  previewInfo: (pid: string) => request<{ pages: number }>('GET', `${P(pid)}/preview`),
  previewPageUrl: (pid: string, n: number, dpi = 80, bust = '') => `${P(pid)}/preview/page/${n}.png?dpi=${dpi}&v=${enc(bust)}`,
  reveal: (pid: string, sub = '') => request<{ path: string }>('POST', `${P(pid)}/reveal`, { sub }),

  // --- формулы и источники
  mathConvert: (value: string, from: 'latex' | 'mathml' | 'omml', to: 'latex' | 'mathml' | 'omml') =>
    request<{ value: string }>('POST', '/api/math/convert', { value, from, to }),
  formatSources: (sources: unknown[], order: string) =>
    request<{ items: { id: string; key: string; text: string; num: number }[] }>('POST', '/api/sources/format', { sources, order }),

  /** Ждёт завершения фонового задания, сообщая ход работы. */
  async waitJob<T = BuildResult>(jid: string, onProgress?: (s: JobState<T>) => void): Promise<T> {
    for (;;) {
      const s = await api.job<T>(jid);
      onProgress?.(s);
      if (s.status === 'done') return s.result as T;
      if (s.status === 'error') throw new ApiError(s.error || 'Ошибка задания', 500);
      if (s.status === 'cancelled') {
        const by = (s as any).cancelledBy as { login: string; admin: boolean } | null;
        throw new ApiError(by?.admin ? `Сборку отменил администратор (${by.login})` : 'Сборка отменена', 499, 'cancelled', { byAdmin: !!by?.admin });
      }
      await new Promise((r) => setTimeout(r, 400));
    }
  },
};
export type Api = typeof api;
